/**
 * @author vishwadeep.kapoor
 * LoginPage actions
 *
 */

import { createStructuredSelector } from 'reselect';
import endpoint from '../../config/endPoints';
import * as reqHandler from '../../config/services/serviceHandler';
import { rootApiResponse, clearDefaultError, fetchUserProfile, defaultAction, loginSuccess, loginError, fetchProfileSuccess } from '../HeaderContainer/actions';
import { CHANGE_LOGIN_FORM, CHANGE_SIGNUP_FORM, CHANGE_FORGOT_FORM, FORGOT_OTP_SUCCESS, OTP_SUBMIT_SUCCESS, CHANGE_SET_PASSWORD_FORM, SET_PASSWORD_SUCCESS,
  SHOW_LOGIN_FORM, OTP_REQUEST_SUCCESS, PRE_BROWSER_PAYLOAD } from './constants';
import { selectLoginForm, selectForgotForm, selectSetPassowrdForm, select_LoginJS } from './selectors';
import { utils, constants, eventTracking } from '../../devUtils';
import { DEFAULT_ACTION, GET_KEYS_OBJ_INARRAY } from '../Profile/constants';

const requestOtpVerification = (payload) => ({ type: OTP_REQUEST_SUCCESS, payload }); // internally setting otp true
const otpSentSuccessAction = (payload) => ({ type: FORGOT_OTP_SUCCESS, payload });
const otpSubmitSuccessAction = (payload) => ({ type: OTP_SUBMIT_SUCCESS, payload });
const setPassowrdSuccessAction = (payload) => ({ type: SET_PASSWORD_SUCCESS, payload });
const controlLoginFormDisplay = (payload) => ({ type: SHOW_LOGIN_FORM, payload });

/**
 * @function displayLoginFrom check dependency and remove async
 * @param {*boolean} payload
 */
export const displayLoginFrom = (payload) => (dispatch) => {
  dispatch(controlLoginFormDisplay(payload));
};
export const changeSetPasswordForm = (prop, value) => ({ type: CHANGE_SET_PASSWORD_FORM, payload: { prop, value } });
export const changeSignUpForm = (prop, value) => ({ type: CHANGE_SIGNUP_FORM, payload: { prop, value } });

const sendOtpAction = (token, device_id, api, phone) => {
  const _api = api;
  _api.defaults.headers.Authorization = utils.addAuthorizationHeaders(token, device_id);
  const payload = { ...{ phone }, ...PRE_BROWSER_PAYLOAD };
  const response = reqHandler.postHandler(api, endpoint.POST_SEND_OTP, payload);
  delete _api.defaults.headers.Authorization;
  return response;
};

const submitOtpRequest = (api, url, payload) => reqHandler.postHandler(api, url, payload, { isPlainJS: true });

const loginSuccessAction = (payload, isLoaderRequired = false) => (dispatch) => {
  dispatch(defaultAction(false));
  dispatch(fetchUserProfile(payload, isLoaderRequired));
  dispatch(displayLoginFrom(false));
  dispatch(loginSuccess());
};

const loginPreParams = ({ username, password }) => {
  const device_id = utils.getDeviceId();
  const body = Object.assign({ username, enc_password: utils.getFinalEncryption(utils.getEncryptedData(password, device_id)), device_id, client_name: 'WebBrowser', client_version: 'WebBrowser' });
  if (/[a-zA-Z]/.test(body.username)) {
    body.email = body.username;
  } else {
    body.phone = body.username;
  }
  return body;
};

/**
 * @function login
 * @description 1) condition of prerequisite
 *              2) sending OTP to user
 */
export const login = ({ isLoaderRequired, g_recaptcha_response_data }) => async (dispatch, getState, api) => {
  const { loginForm } = createStructuredSelector({ loginForm: selectLoginForm() })(getState());
  try {
    dispatch(defaultAction());
    dispatch(clearDefaultError());
    const payload = { g_recaptcha_response_data, ...loginPreParams(loginForm) };
    const { device_id } = payload;
    const {
      user,
      user: { user_client_id = '' } = {},
      message,
      prerequisite,
      token,
    } = await reqHandler.postHandler(api, endpoint.POST_BROWSER_LOGIN, payload);
    const setLoginResp = { token, device_id, client_id: user_client_id };
    await reqHandler.postHandler(api, endpoint.POST_SET_TOKEN, setLoginResp);
    if (user && (prerequisite == null || (typeof prerequisite === 'object' && Object.keys(prerequisite).length === 0))) { /** @desc if login success fetch user profile */
      dispatch(loginSuccessAction({ token, device_id, client_id: user_client_id }, isLoaderRequired));
      eventTracking.triggerEvent('Login', 'Submit', 'Success');
    } else if (user && prerequisite) { /** @desc submit otp and redirect to otp screen handling by @func SIGNUP */
      const { phone_verification, email, phone } = prerequisite;
      if (email && email.status === 'required') {
        const _phone = phone_verification && phone_verification.phone ? phone_verification.phone : user && user.phone;
        const { message: _message } = await sendOtpAction(token, device_id, api, _phone);
        // const { message: _message } = await sendOtpAction(token, device_id, api, phone_verification.phone);
        dispatch(rootApiResponse(utils.errorResponse('AUTO_SIGNUP', _message, 'popup')));
        dispatch(requestOtpVerification({ success: true, type: 'email_verification', response: { token, device_id, user } }));
      } else if (phone && phone.status === 'required') {
        dispatch(rootApiResponse(utils.errorResponse('AUTO_SIGNUP', 'Please Enter your phone number to verify', 'popup')));
        dispatch(requestOtpVerification({ success: true, type: 'phone_verification', otpSent: false, response: { token, device_id, user } }));
      } else if (phone_verification && phone_verification.status === 'required') {
        const { message: _message } = await sendOtpAction(token, device_id, api, phone_verification.phone);
        dispatch(rootApiResponse(utils.errorResponse('AUTO_SIGNUP', _message, 'popup')));
        dispatch(requestOtpVerification({ success: true, type: 'otp_verification', response: { token, device_id, user } }));
      }
    } else { /** @property handles the incorrect and rest @param {*string} */
      throw (message);
    }
  } catch (err) {
    dispatch(loginError());
    dispatch(rootApiResponse(utils.errorResponse('LOGIN', err, 'popup')));
    eventTracking.triggerEvent('Login', 'Submit', 'Failure');
  }
};

export const verifyUserMobileNumber = (payload, { token, device_id }) => async (dispatch, getState, api) => {
  try {
    dispatch(clearDefaultError());
    const { phone, user } = payload;
    const { message: _message } = await sendOtpAction(token, device_id, api, phone);
    dispatch(rootApiResponse(utils.errorResponse('OTP', _message, 'popup')));
    dispatch(requestOtpVerification({ success: true, type: 'phone_verification', otpSent: true, response: { token, device_id, user }, ...phone }));
  } catch (err) {
    dispatch(rootApiResponse(utils.errorResponse('OTP', err, 'popup')));
  }
};
export const sendSignUpOtp = (payload) => async (dispatch, getState, api) => {
  try {
    dispatch(clearDefaultError());
    const { phone, token, device_id } = payload;
    const { message: _message } = await sendOtpAction(token, device_id, api, phone);
    dispatch(rootApiResponse(utils.successResponse('RESEND_SIGNUP_OTP', _message, 'popup')));
  } catch (err) {
    dispatch(rootApiResponse(utils.errorResponse('RESEND_SIGNUP_OTP', err, 'popup')));
  }
};

export const changeLoginForm = (prop, value) => (dispatch) => {
  dispatch(clearDefaultError());
  dispatch({
    type: CHANGE_LOGIN_FORM,
    payload: { prop, value },
  });
};

export const setPasswordSubmit = (type) => async (dispatch, getState, api) => {
  try {
    const device_id = utils.getDeviceId();
    dispatch(clearDefaultError());
    const { setPasswordForm } = createStructuredSelector({ setPasswordForm: selectSetPassowrdForm() })(getState());
    const data = {
      [type]: setPasswordForm[type],
    };
    const _data = { ...data, ...{ device_id, client_name: 'WebBrowser', app_version: '1.0' } };
    const response = await reqHandler.postHandler(api, endpoint.POST_SET_PASSWORD, _data);
    eventTracking.triggerEvent('Login', 'Set password - confirm', 'Success');
    dispatch(rootApiResponse(utils.successResponse('SET PASSWORD', response.message, 'popup')));
    dispatch(setPassowrdSuccessAction(true));
  } catch (err) {
    eventTracking.triggerEvent('Login', 'Set password - confirm', 'Failure');
    dispatch(rootApiResponse(utils.errorResponse('SET PASSWORD', err, 'popup')));
  }
};

export const changeForgotForm = (prop, value) => ({ type: CHANGE_FORGOT_FORM, payload: { prop, value } });

export const forgotSubmit = (status) => async (dispatch, getState, api) => {
  try {
    const device_id = utils.getDeviceId();
    dispatch(clearDefaultError());
    const { forgotForm } = createStructuredSelector({ forgotForm: selectForgotForm() })(getState());
    if (status === 'otpSubmit') {
      if (forgotForm.password === forgotForm.confirmPassword) {
        const _forgotForm = { ...forgotForm, ...{ device_id, client_name: 'WebBrowser', app_version: '1.0' } };
        const response = await submitOtpRequest(api, endpoint.POST_PASSWORD_REESET, _forgotForm);
        if (response && Object.keys(response).length === 0) {
          dispatch(rootApiResponse(utils.successResponse('FORGOT_PASSWORD', constants.PASSWORD_RESET_SUCCESS)));
          dispatch(otpSubmitSuccessAction(true));
          eventTracking.triggerEvent('Login', 'Forgot password - confirm', 'Success');
        } else {
          eventTracking.triggerEvent('Login', 'Forgot password - confirm', 'Failure');
          throw (response.message);
        }
      } else {
        throw (constants.PASSWORD_MATCH);
      }
    } else {
      const _forgotForm = { ...forgotForm, ...{ device_id, client_name: 'WebBrowser', app_version: '1.0' } };
      const response = await reqHandler.postHandler(api, endpoint.POST_BROWSER_PASSWORD, _forgotForm);
      if (response.message) {
        dispatch(rootApiResponse(utils.successResponse('FORGOT_OTP', response.message, 'popup')));
        dispatch(otpSentSuccessAction(true));
        eventTracking.triggerEvent('Login', 'Forgot Password Send OTP', 'Success');
        // dispatch(otpSubmitSuccessAction(false));
      } else {
        eventTracking.triggerEvent('Login', 'Forgot Password Send OTP', 'Failure');
        throw (response.message);
      }
    }
  } catch (err) {
    dispatch(rootApiResponse(utils.errorResponse('FORGOT_PASSWORD', err, 'popup')));
  }
};

const signUp = (params = {}) => async (dispatch, getState, api) => {
  try {
    dispatch(clearDefaultError());
    dispatch(defaultAction());
    const device_id = utils.getDeviceId();
    const { pathname } = params;
    const client_series = pathname === constants.LINK_CLIENT_MANIPAL ? endpoint.MANIPAL_SERIES : '';
    const { signUpform } = createStructuredSelector({ signUpform: select_LoginJS('signUpform') })(getState());
    const payload = { ...signUpform, ...{ device_id, client_name: 'WebBrowser', client_version: 'WebBrowser', client_key: 'WebBrowser', client_series } };
    const response = await reqHandler.postHandler(api, endpoint.POST_REGISTRATION, payload);
    if (response.message || response.mesage) {
      throw (response.message || response.mesage);
    } else {
      response.device_id = device_id;
      dispatch(rootApiResponse(utils.successResponse('SIGNUP', constants.ALERT_SIGNUP, 'popup'))); // null condition for making loader false
      eventTracking.triggerEvent('Signup', 'Submit', 'Success');
      dispatch(requestOtpVerification({ success: true, response })); // internally handling submit OTP function
    }
  } catch (err) {
    // dispatch(changeSignUpForm('name', ''));
    // dispatch(changeSignUpForm('phone', ''));
    dispatch(rootApiResponse(utils.errorResponse('SIGNUP', err, 'popup')));
    eventTracking.triggerEvent('Signup', 'Submit', 'Failure');
  }
};

export const autoSignupUpdateDetails = (payload) => (dispatch) => {
  const { token, device_id } = payload;
  dispatch(saveProfileChange({ email: payload.data.email }, { token, device_id }));
};

/** @todo commented @func defaultAction as it loading the login again */
export const signUpOtpSubmitForm = (payload) => async (dispatch, getState, api) => {
  const _api = api;
  try {
    dispatch(clearDefaultError());
    // dispatch(defaultAction());
    const { token, device_id, skipSignup } = payload;
    const _payload = { ...payload.data, ...{ device_id, client_name: 'WebBrowser', app_version: '1.0' } };
    _api.defaults.headers.authorization = utils.addAuthorizationHeaders(token, device_id);
    const response = await submitOtpRequest(_api, endpoint.POST_SIGNUP_OTP, _payload);
    delete _api.defaults.headers.authorization;
    if (response && Object.keys(response).length === 0) {
      if (skipSignup && skipSignup) {
        // dispatch(saveProfileChange({ email: payload.data.email }, { token, device_id }));
        dispatch(loginSuccessAction({ token, device_id }));
      }
      dispatch(otpSubmitSuccessAction(true));
      dispatch(rootApiResponse(utils.successResponse('SIGNUP_OTP', constants.ALERT_SIGNUP_OTP_VERIFIED, 'popup')));
      eventTracking.triggerEvent('Signup', 'OTP Verification', 'Success');
    } else {
      throw (response.message);
    }
  } catch (err) {
    delete _api.defaults.headers.authorization;
    dispatch(rootApiResponse(utils.errorResponse('OTP', err, 'popup')));
    eventTracking.triggerEvent('Signup', 'OTP Verification', 'Failure');
  }
};

export const saveProfileChange = (payload, otherParams) => async (dispatch, getState, api) => {
  try {
    dispatch(utils.isReducerLoading(DEFAULT_ACTION, true));
    dispatch(clearDefaultError());
    // const authPrerequist = utils.getAuthPrerequist(getState, true);
    let authPrerequist = utils.getAuthPrerequist(getState, true);
    if (otherParams) {
      authPrerequist = { ...authPrerequist, ...otherParams.token };
    }
    const response = await reqHandler.putHandler(api, endpoint.PUT_USER_PROFILE, payload, authPrerequist);
    if (response.message !== 'Profile updated succesfully') {
      throw (response.message);
    } else {
      dispatch(utils.isReducerLoading(DEFAULT_ACTION));
      dispatch(rootApiResponse(utils.successResponse(DEFAULT_ACTION, constants.ALERT_PROFILE_UPDATED, otherParams && 'popup')));
      eventTracking.triggerEvent('My Account', 'Update Profile', `${GET_KEYS_OBJ_INARRAY(payload)}--<Success>`);
      const { device_id, token } = authPrerequist;
      const user = { ...response.user, device_id, token };
      dispatch(fetchProfileSuccess(user));
      if (otherParams) {
        dispatch(signUpOtpSubmitForm(otherParams.others));
      }
    }
  } catch (err) {
    dispatch(utils.isReducerLoading(DEFAULT_ACTION));
    eventTracking.triggerEvent('My Account', 'Update Profile', `${GET_KEYS_OBJ_INARRAY(payload)}--<Failure>`);
    dispatch(rootApiResponse(utils.errorResponse(DEFAULT_ACTION, err, otherParams && 'popup')));
  }
};

export { signUp };



// WEBPACK FOOTER //
// client/containers/LoginPage/actions.js